import React, {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import CreateAppointmentOverlay from '@/modules/appointment/components/CreateAppointmentOverlay';
import type { AppointmentCreatePrefill } from '@/modules/appointment/utils/appointmentViewParams';
import type { AddLeadPrefill } from '@/modules/lead/utils/leadForm';
import type { AddPatientPrefill } from '@/modules/patient/utils/patientForm';
import type { TaskCreatePrefill } from '@/modules/task/utils/taskViewParams';
import {
  resolveCreateHostAction,
  type CreateHostOpen,
} from './resolveCreateHostAction';

const CreatePatientDialog = lazy(
  () => import('@/modules/patient/components/CreatePatientDialog'),
);
const CreateLeadDialog = lazy(
  () => import('@/modules/lead/components/CreateLeadDialog'),
);
const ProjectFormDialog = lazy(
  () => import('@/modules/project/components/ProjectFormDialog'),
);
const ExpenseFormDialog = lazy(
  () => import('@/modules/expense/components/ExpenseFormDialog'),
);
const TaskDetailDialog = lazy(
  () => import('@/modules/task/components/TaskDetailDialog'),
);
const RequestPayrollAdvanceDialog = lazy(
  () => import('@/modules/payroll/components/RequestPayrollAdvanceDialog'),
);
const TreatmentCatalogItemFormDialog = lazy(
  () => import('@/modules/treatment-catalog/components/TreatmentCatalogItemFormDialog'),
);

type AppointmentSession = {
  id: number;
  open: boolean;
  prefill: AppointmentCreatePrefill;
};

type PatientSession = {
  id: number;
  open: boolean;
  prefill?: AddPatientPrefill;
};

type LeadSession = {
  id: number;
  open: boolean;
  prefill?: AddLeadPrefill;
};

type TaskSession = {
  id: number;
  open: boolean;
  prefill: TaskCreatePrefill;
};

type CreateDialogHostContextValue = {
  openAppointmentCreate: (prefill?: AppointmentCreatePrefill) => void;
  openPatientCreate: (prefill?: AddPatientPrefill) => void;
  openLeadCreate: (prefill?: AddLeadPrefill) => void;
  openTaskCreate: (prefill?: TaskCreatePrefill) => void;
  openProjectCreate: () => void;
  openExpenseCreate: () => void;
  openAdvanceRequest: () => void;
  openTreatmentCatalogCreate: () => void;
  /** Spotlight / shortcut / recent — returns true when handled in-place. */
  tryOpenCreate: (
    actionId: string,
    opts?: { state?: object; path?: string },
  ) => boolean;
};

const CreateDialogHostContext = createContext<CreateDialogHostContextValue | null>(
  null,
);

const applyOpen = (
  open: CreateHostOpen,
  api: Omit<CreateDialogHostContextValue, 'tryOpenCreate'>,
): void => {
  switch (open.kind) {
    case 'appointment':
      api.openAppointmentCreate(open.prefill);
      return;
    case 'patient':
      api.openPatientCreate(open.prefill);
      return;
    case 'lead':
      api.openLeadCreate(open.prefill);
      return;
    case 'task':
      api.openTaskCreate(open.prefill);
      return;
    case 'project':
      api.openProjectCreate();
      return;
    case 'expense':
      api.openExpenseCreate();
      return;
    case 'advance-request':
      api.openAdvanceRequest();
      return;
    case 'treatment-catalog':
      api.openTreatmentCatalogCreate();
      return;
  }
};

/**
 * Layout host for create dialogs opened from Spotlight / shortcuts / module CTAs.
 */
export const CreateDialogHostProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [appointment, setAppointment] = useState<AppointmentSession>({
    id: 0,
    open: false,
    prefill: {},
  });
  const [patient, setPatient] = useState<PatientSession>({
    id: 0,
    open: false,
  });
  const [lead, setLead] = useState<LeadSession>({
    id: 0,
    open: false,
  });
  const [task, setTask] = useState<TaskSession>({
    id: 0,
    open: false,
    prefill: {},
  });
  const [projectOpen, setProjectOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [advanceRequestOpen, setAdvanceRequestOpen] = useState(false);
  const [treatmentCatalogOpen, setTreatmentCatalogOpen] = useState(false);

  const openAppointmentCreate = useCallback((prefill: AppointmentCreatePrefill = {}) => {
    setAppointment((current) => ({
      id: current.id + 1,
      open: true,
      prefill,
    }));
  }, []);

  const openPatientCreate = useCallback((prefill?: AddPatientPrefill) => {
    setPatient((current) => ({
      id: current.id + 1,
      open: true,
      prefill,
    }));
  }, []);

  const openLeadCreate = useCallback((prefill?: AddLeadPrefill) => {
    setLead((current) => ({
      id: current.id + 1,
      open: true,
      prefill,
    }));
  }, []);

  const openTaskCreate = useCallback((prefill: TaskCreatePrefill = {}) => {
    setTask((current) => ({
      id: current.id + 1,
      open: true,
      prefill,
    }));
  }, []);

  const openProjectCreate = useCallback(() => setProjectOpen(true), []);
  const openExpenseCreate = useCallback(() => setExpenseOpen(true), []);
  const openAdvanceRequest = useCallback(() => setAdvanceRequestOpen(true), []);
  const openTreatmentCatalogCreate = useCallback(
    () => setTreatmentCatalogOpen(true),
    [],
  );

  const tryOpenCreate = useCallback(
    (actionId: string, opts?: { state?: object; path?: string }): boolean => {
      const resolved = resolveCreateHostAction(actionId, opts);
      if (!resolved) return false;
      applyOpen(resolved, {
        openAppointmentCreate,
        openPatientCreate,
        openLeadCreate,
        openTaskCreate,
        openProjectCreate,
        openExpenseCreate,
        openAdvanceRequest,
        openTreatmentCatalogCreate,
      });
      return true;
    },
    [
      openAppointmentCreate,
      openPatientCreate,
      openLeadCreate,
      openTaskCreate,
      openProjectCreate,
      openExpenseCreate,
      openAdvanceRequest,
      openTreatmentCatalogCreate,
    ],
  );

  const value = useMemo(
    () => ({
      openAppointmentCreate,
      openPatientCreate,
      openLeadCreate,
      openTaskCreate,
      openProjectCreate,
      openExpenseCreate,
      openAdvanceRequest,
      openTreatmentCatalogCreate,
      tryOpenCreate,
    }),
    [
      openAppointmentCreate,
      openPatientCreate,
      openLeadCreate,
      openTaskCreate,
      openProjectCreate,
      openExpenseCreate,
      openAdvanceRequest,
      openTreatmentCatalogCreate,
      tryOpenCreate,
    ],
  );

  return (
    <CreateDialogHostContext.Provider value={value}>
      {children}

      <CreateAppointmentOverlay
        key={`create-host-appt-${appointment.id}`}
        open={appointment.open}
        onClose={() => setAppointment((c) => ({ ...c, open: false }))}
        patientId={appointment.prefill.patientId}
        leadId={appointment.prefill.leadId}
        clinicId={appointment.prefill.clinicId}
        doctorId={appointment.prefill.doctorId}
        date={appointment.prefill.date}
        startTime={appointment.prefill.startTime}
        showPrefillWarnBanner={appointment.prefill.warn}
      />

      {patient.open ? (
        <Suspense fallback={null}>
          <CreatePatientDialog
            key={`create-host-patient-${patient.id}`}
            prefill={patient.prefill}
            onClose={() => setPatient((c) => ({ ...c, open: false }))}
          />
        </Suspense>
      ) : null}

      {lead.open ? (
        <Suspense fallback={null}>
          <CreateLeadDialog
            key={`create-host-lead-${lead.id}`}
            open
            prefill={lead.prefill}
            onClose={() => setLead((c) => ({ ...c, open: false }))}
          />
        </Suspense>
      ) : null}

      {task.open ? (
        <Suspense fallback={null}>
          <TaskDetailDialog
            key={`create-host-task-${task.id}`}
            open
            taskId={null}
            createMode
            createPrefill={task.prefill}
            onClose={() => setTask((c) => ({ ...c, open: false }))}
            onCreated={() => setTask((c) => ({ ...c, open: false }))}
          />
        </Suspense>
      ) : null}

      {projectOpen ? (
        <Suspense fallback={null}>
          <ProjectFormDialog open onClose={() => setProjectOpen(false)} />
        </Suspense>
      ) : null}

      {expenseOpen ? (
        <Suspense fallback={null}>
          <ExpenseFormDialog
            open
            onClose={() => setExpenseOpen(false)}
            onSuccess={() => setExpenseOpen(false)}
          />
        </Suspense>
      ) : null}

      {advanceRequestOpen ? (
        <Suspense fallback={null}>
          <RequestPayrollAdvanceDialog
            open
            onClose={() => setAdvanceRequestOpen(false)}
          />
        </Suspense>
      ) : null}

      {treatmentCatalogOpen ? (
        <Suspense fallback={null}>
          <TreatmentCatalogItemFormDialog
            mode="create"
            onClose={() => setTreatmentCatalogOpen(false)}
            onSuccess={() => setTreatmentCatalogOpen(false)}
          />
        </Suspense>
      ) : null}
    </CreateDialogHostContext.Provider>
  );
};

export const useCreateDialogHost = (): CreateDialogHostContextValue => {
  const ctx = useContext(CreateDialogHostContext);
  if (!ctx) {
    throw new Error('useCreateDialogHost must be used within CreateDialogHostProvider');
  }
  return ctx;
};
